import React, { useCallback, useEffect, useState } from 'react'
import { Button, Card, CardHeader, CardTitle } from '@duck/ui'
import { usePartnerTar } from '@/hooks/usePartnerTar'
import { CohortPeopleTable } from './CohortPeopleTable'
import { CohortPersonDetails } from '../cohortPageData'
import { NoDataEmptyState } from '@/components/EmptyStates'
import { DeleteDialog } from '@/components/DeleteDialog/DeleteDialog'
import { UserGroupIcon, TrashIcon } from '@heroicons/react/24/outline'
import { CohortPeopleListSkeleton } from './CohortPeopleListSkeleton'
import { UserService } from '@duck/api-definitions'
import { apis } from '@/modules/apiClients'
import { useTar } from '@/modules/i18n/useTar'

interface CohortPeopleProps {
  listTitle: string
  peopleDetails: CohortPersonDetails[]
  persons: UserService.Person[]
  cohortId: string
  handleCohortDataChange?: () => void
  listMode?: 'all' | 'task'
  skeleton?: boolean
  onAddPeople?: () => void
  onCheckedPersonIdsChange?: (checked: Set<string>) => void
  /** Map of personId -> number of assignments (for remove dialog) */
  assignmentCountByPersonId?: Record<string, number>
}

export const CohortPeopleList: React.FC<CohortPeopleProps> = ({
  listTitle,
  persons,
  peopleDetails,
  cohortId,
  listMode = 'all',
  skeleton = false,
  onAddPeople,
  onCheckedPersonIdsChange,
  handleCohortDataChange,
  assignmentCountByPersonId = {},
}) => {
  const personIds = peopleDetails.map(person => person.personId)
  const { t: tP } = usePartnerTar()
  const { t } = useTar()
  const [checkedPersons, setCheckedPersonsState] = useState<Set<string>>(new Set())

  // Sync to parent when local selection changes (do not call parent setState during our render/updater)
  useEffect(() => {
    onCheckedPersonIdsChange?.(checkedPersons)
  }, [checkedPersons, onCheckedPersonIdsChange])

  const setCheckedPersons = useCallback((value: React.SetStateAction<Set<string>>) => {
    setCheckedPersonsState(prev => (value instanceof Function ? value(prev) : value))
  }, [])

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const handleRemovePeople = async () => {
    await apis.partnerService.cohortsRemovePeoplePost({
      cohortId,
      peopleIds: Array.from(checkedPersons),
    })
    setCheckedPersons(new Set())
    handleCohortDataChange?.()
  }

  const deleteHeaderAction =
    checkedPersons.size > 0 ? (
      <Button
        variant="ghost"
        size="xs"
        className="removePeople text-destructive hover:text-destructive hover:bg-destructive/10 gap-2 m-0"
        data-testid="removePeople"
        onClick={() => setIsDeleteDialogOpen(true)}
      >
        <TrashIcon className="h-4 w-4" />
        {t('common.remove')}
      </Button>
    ) : <div className="h-6" />

  const CardWrapper = ({ children, headerAction }: { children: React.ReactNode; headerAction?: React.ReactNode }) => {
    return (
      <Card>
        <CardHeader className={headerAction ? "flex-row items-center justify-between" : ""}>
          <CardTitle>{listTitle}</CardTitle>
          {headerAction}
        </CardHeader>
        {children}
      </Card>
    )
  }

  if (skeleton) {
    return <CohortPeopleListSkeleton listTitle={listTitle} />
  }

  if (!personIds || personIds.length === 0) {
    return (
      <CardWrapper>
        <NoDataEmptyState
          icon={UserGroupIcon}
          title={tP('noPeopleYet')}
          description={tP('noPeopleDescription')}
          buttonText={listMode === 'all' ? tP('addPeople') : undefined}
          onButtonClick={listMode === 'all' ? onAddPeople : undefined}
        />
      </CardWrapper>
    )
  }

  const checkedWithAssignments = peopleDetails.filter(
    p => checkedPersons.has(p.personId) && p.assignmentStatus != null
  )
  const deleteDialog = (
    <DeleteDialog
      open={isDeleteDialogOpen}
      onOpenChange={setIsDeleteDialogOpen}
      title={tP('removePeopleTitle')}
      description={checkedWithAssignments.length > 0 ? t('partner.removePeopleWithAssignmentsDescription') : t('partner.removePeopleDescription')}
      deleteButtonText={tP('removePeople')}
      onDelete={handleRemovePeople}
    >
      {checkedWithAssignments.length > 0 && (
        <div className="mt-3 rounded-md border border-border bg-muted/10 p-3">
          <p className="text-sm font-medium text-foreground mb-2">
            {tP('removePeopleWithAssignmentsCount', { count: checkedWithAssignments.length })}
          </p>
          <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
            {checkedWithAssignments.map((detail) => {
              const person = persons.find(per => per.id === detail.personId)
              const name = person ? `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim() || person.email : detail.email
              const count = assignmentCountByPersonId[detail.personId] ?? 1
              return (
                <li key={detail.personId}>
                  {name} {tP('assignmentCountSuffix', { count })}
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </DeleteDialog>
  )

  if (listMode === 'task') {
    return (
      <>
        <CardWrapper headerAction={deleteHeaderAction}>
          <CohortPeopleTable
            cohortId={cohortId}
            peopleDetails={peopleDetails}
            persons={persons}
            checkedPersons={checkedPersons}
            setCheckedPersons={setCheckedPersons}
            view="task"
          />
        </CardWrapper>
        {deleteDialog}
      </>
    )
  }

  return (
    <>
      <CardWrapper headerAction={deleteHeaderAction}>
        <CohortPeopleTable
          cohortId={cohortId}
          peopleDetails={peopleDetails}
          persons={persons}
          checkedPersons={checkedPersons}
          setCheckedPersons={setCheckedPersons}
        />
      </CardWrapper>
      {deleteDialog}
    </>
  )
}
